import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { authAPI } from './services/api';
import { useToast } from './hooks/useToast';
import ToastContainer from './components/ToastContainer';
import DevLoginDropdown from './components/DevLoginDropdown';
import ScrollToTop from './components/ScrollToTop';
import MetaPixelTracker from './components/MetaPixelTracker';
import CookieConsent from './components/CookieConsent';
import './App.css';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import SetupProfilePage from './pages/SetupProfilePage';
import UserDashboard from './pages/UserDashboard';
import AdminDashboard from './pages/AdminDashboard';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import CompanySettingsPage from './pages/CompanySettingsPage';
import BikeDetailPage from './pages/BikeDetailPage';
import ProfilePage from './pages/ProfilePage';
import BookingCalendarPage from './pages/BookingCalendarPage';
import BookingWizardPage from './pages/BookingWizardPage';
import OnboardingWizard from './pages/OnboardingWizard';
import BillingPage from './pages/BillingPage';
import CompanyListPage from './pages/CompanyListPage';
import CompanyPublicProfilePage from './pages/CompanyPublicProfilePage';
import CompanyAchievementsPage from './pages/CompanyAchievementsPage';
import CompanyBookingsCalendar from './pages/CompanyBookingsCalendar';
import TechnicianDashboard from './pages/TechnicianDashboard';
import AdminManagementPage from './pages/AdminManagementPage';
import LandingPage from './pages/LandingPage';
import AnalyticsPage from './pages/AnalyticsPage';
import SupportPage from './pages/SupportPage';
import MyBikesPage from './pages/MyBikesPage';
import HelpPage from './pages/HelpPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';

const getStoredDarkModePreference = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }
  const saved = window.localStorage.getItem('darkMode');
  return saved !== null ? JSON.parse(saved) : null;
};

const setStoredDarkModePreference = (value) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  window.localStorage.setItem('darkMode', JSON.stringify(value));
};

const getSystemPrefersDark = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return true;
  }
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  return mediaQuery ? mediaQuery.matches : true;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requiresSetup, setRequiresSetup] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const stored = getStoredDarkModePreference();
    return stored !== null ? stored : getSystemPrefersDark();
  });
  const [hasManualDarkModePreference, setHasManualDarkModePreference] = useState(() => getStoredDarkModePreference() !== null);
  const manualPreferenceRef = useRef(hasManualDarkModePreference);
  const toast = useToast();

  useEffect(() => {
    manualPreferenceRef.current = hasManualDarkModePreference;
  }, [hasManualDarkModePreference]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    if (!mediaQuery) {
      return undefined;
    }

    const handleSystemThemeChange = (event) => {
      if (manualPreferenceRef.current) {
        return;
      }
      setDarkMode(event.matches);
    };

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleSystemThemeChange);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(handleSystemThemeChange);
    }

    return () => {
      if (typeof mediaQuery.removeEventListener === 'function') {
        mediaQuery.removeEventListener('change', handleSystemThemeChange);
      } else if (typeof mediaQuery.removeListener === 'function') {
        mediaQuery.removeListener(handleSystemThemeChange);
      }
    };
  }, []);

  // Toggle dark mode
  const toggleDarkMode = useCallback(() => {
    setDarkMode(prev => {
      const newValue = !prev;
      setStoredDarkModePreference(newValue);
      return newValue;
    });
    setHasManualDarkModePreference(true);
  }, []);


  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    // Failsafe: never keep the initial loading overlay forever.
    const hardStopLoading = setTimeout(() => {
      setLoading(false);
    }, 15000);

    return () => clearTimeout(hardStopLoading);
  }, []);

  const checkAuth = async () => {
    let timeoutId = null;
    try {
      const timeoutPromise = new Promise((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error('Auth check timeout')), 12000);
      });
      const response = await Promise.race([authAPI.checkAuth(), timeoutPromise]);
      if (response.data && response.data.authenticated) {
        setUser(response.data.user);
        setRequiresSetup(response.data.requires_setup || false);

        // Check if company needs onboarding (for company admins)
        if (response.data.user && (response.data.user.role === 'company_admin' || response.data.user.role === 'admin')) {
          try {
            const { companyAPI } = await import('./services/api');
            const statsResponse = await companyAPI.getCompanyStats();
            setNeedsOnboarding(statsResponse.data.needs_onboarding || false);
          } catch (err) {
            // If check fails, don't block
            console.log('Could not check onboarding status');
          }
        }

      }
    } catch (error) {
      console.error('Auth check failed:', error);
      // Don't block rendering if auth check fails - user might not be logged in
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      setLoading(false);
    }
  };

  const handleLogin = async (userData, needsSetup = false) => {
    setUser(userData);
    setRequiresSetup(needsSetup);

    // Check onboarding status for company admins
    if (userData && (userData.role === 'company_admin' || userData.role === 'admin')) {
      try {
        const { companyAPI } = await import('./services/api');
        const statsResponse = await companyAPI.getCompanyStats();
        setNeedsOnboarding(statsResponse.data.needs_onboarding || false);
      } catch (err) {
        console.log('Could not check onboarding status');
      }
    }

  };

  const handleLogout = async () => {
    try {
      await authAPI.logout();
      setUser(null);
      setRequiresSetup(false);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleProfileSetup = (userData) => {
    setUser(userData);
    setRequiresSetup(false);
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <Router>
      <ScrollToTop />
      <MetaPixelTracker />
      <div className="App">
        <ToastContainer toasts={toast.toasts} removeToast={toast.removeToast} darkMode={darkMode} />
        <CookieConsent darkMode={darkMode} />
        {/* Dev Quick Login - only visible in development */}
        <DevLoginDropdown onLogin={handleLogin} currentUser={user} />

        <Routes>
          <Route
            path="/login"
            element={
              user ? (
                requiresSetup ? <Navigate to="/setup-profile" /> : <Navigate to="/dashboard" />
              ) : (
                <LoginPage onLogin={handleLogin} />
              )
            }
          />
          <Route
            path="/companies"
            element={user ? <CompanyListPage user={user} onLogout={handleLogout} darkMode={darkMode} onToggleDarkMode={toggleDarkMode} /> : <Navigate to="/login" />}
          />
          <Route
            path="/companies/:companyId"
            element={user ? <CompanyPublicProfilePage user={user} onLogout={handleLogout} darkMode={darkMode} onToggleDarkMode={toggleDarkMode} /> : <Navigate to="/login" />}
          />
          <Route
            path="/company/achievements"
            element={user ? <CompanyAchievementsPage user={user} onLogout={handleLogout} darkMode={darkMode} onToggleDarkMode={toggleDarkMode} /> : <Navigate to="/login" />}
          />
          <Route
            path="/register"
            element={<Navigate to="/onboarding" replace />}
          />
          <Route
            path="/forgot-password"
            element={user ? <Navigate to="/dashboard" /> : <ForgotPasswordPage />}
          />
          <Route
            path="/reset-password"
            element={user ? <Navigate to="/dashboard" /> : <ResetPasswordPage />}
          />
          <Route
            path="/setup-profile"
            element={
              user && requiresSetup ? (
                <SetupProfilePage onSetup={handleProfileSetup} onLogout={handleLogout} />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="/onboarding"
            element={
              user && needsOnboarding ? (
                <OnboardingWizard
                  user={user}
                  onLogin={handleLogin}
                  onLogout={handleLogout}
                  onComplete={() => {
                    setNeedsOnboarding(false);
                  }}
                />
              ) : !user ? (
                <OnboardingWizard
                  user={null}
                  onLogin={handleLogin}
                  onLogout={handleLogout}
                  onComplete={() => {
                    // Just navigate, no state to update for local user?
                    // Actually OnboardingWizard handles navigation now.
                  }}
                />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="/dashboard"
            element={
              user ? (
                requiresSetup ? (
                  <Navigate to="/setup-profile" />
                ) : needsOnboarding && (user.role === 'company_admin' || user.role === 'admin') ? (
                  <Navigate to="/onboarding" />
                ) : user.role === 'super_admin' ? (
                  <SuperAdminDashboard user={user} onLogout={handleLogout} darkMode={darkMode} onToggleDarkMode={toggleDarkMode} />
                ) : user.role === 'company_admin' || user.role === 'admin' ? (
                  <AdminDashboard user={user} onLogout={handleLogout} darkMode={darkMode} onToggleDarkMode={toggleDarkMode} />
                ) : user.role === 'technician' ? (
                  <TechnicianDashboard user={user} onLogout={handleLogout} darkMode={darkMode} onToggleDarkMode={toggleDarkMode} />
                ) : (
                  <UserDashboard user={user} onLogout={handleLogout} darkMode={darkMode} onToggleDarkMode={toggleDarkMode} />
                )
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/admin"
            element={
              user && user.role === 'super_admin' ? (
                <SuperAdminDashboard user={user} onLogout={handleLogout} darkMode={darkMode} onToggleDarkMode={toggleDarkMode} />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="/admin/companies"
            element={
              user && user.role === 'super_admin' ? (
                <SuperAdminDashboard user={user} onLogout={handleLogout} darkMode={darkMode} onToggleDarkMode={toggleDarkMode} />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="/admin/company-settings"
            element={
              user && (user.role === 'company_admin' || user.role === 'super_admin') ? (
                <CompanySettingsPage user={user} onLogout={handleLogout} darkMode={darkMode} onToggleDarkMode={toggleDarkMode} />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="/admin/management"
            element={
              user && (user.role === 'company_admin' || user.role === 'admin' || user.role === 'technician' || user.role === 'super_admin') ? (
                <AdminManagementPage user={user} onLogout={handleLogout} darkMode={darkMode} onToggleDarkMode={toggleDarkMode} />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="/admin/analytics"
            element={
              user && (user.role === 'company_admin' || user.role === 'admin' || user.role === 'super_admin') ? (
                <AnalyticsPage user={user} onLogout={handleLogout} darkMode={darkMode} onToggleDarkMode={toggleDarkMode} />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="/bike/:bikeId"
            element={user ? <BikeDetailPage user={user} onLogout={handleLogout} darkMode={darkMode} onToggleDarkMode={toggleDarkMode} onUserUpdate={setUser} /> : <Navigate to="/login" />}
          />
          <Route
            path="/my-bikes"
            element={user ? <MyBikesPage user={user} onLogout={handleLogout} darkMode={darkMode} onToggleDarkMode={toggleDarkMode} /> : <Navigate to="/login" />}
          />
          <Route
            path="/profile"
            element={user ? <ProfilePage user={user} onLogout={handleLogout} darkMode={darkMode} onToggleDarkMode={toggleDarkMode} onUserUpdate={setUser} /> : <Navigate to="/login" />}
          />
          <Route
            path="/calendar"
            element={
              user ? (
                user.role === 'technician' ? (
                  <Navigate to="/dashboard" />
                ) : user.role === 'company_admin' || user.role === 'super_admin' ? (
                  <CompanyBookingsCalendar user={user} onLogout={handleLogout} darkMode={darkMode} onToggleDarkMode={toggleDarkMode} />
                ) : (
                  <BookingCalendarPage user={user} onLogout={handleLogout} darkMode={darkMode} onToggleDarkMode={toggleDarkMode} />
                )
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/book"
            element={
              user ? (
                user.role === 'technician' ? (
                  <Navigate to="/dashboard" />
                ) : (
                  <BookingWizardPage user={user} onLogout={handleLogout} darkMode={darkMode} onToggleDarkMode={toggleDarkMode} />
                )
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/billing"
            element={user ? <BillingPage user={user} onLogout={handleLogout} darkMode={darkMode} onToggleDarkMode={toggleDarkMode} /> : <Navigate to="/login" />}
          />
          <Route
            path="/support"
            element={user ? <SupportPage user={user} onLogout={handleLogout} darkMode={darkMode} onToggleDarkMode={toggleDarkMode} /> : <Navigate to="/login" />}
          />
          <Route
            path="/help"
            element={user ? <HelpPage user={user} onLogout={handleLogout} darkMode={darkMode} onToggleDarkMode={toggleDarkMode} /> : <Navigate to="/login" />}
          />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/" element={<LandingPage user={user} onLogout={handleLogout} onLogin={handleLogin} />} />
          <Route path="/landing" element={<LandingPage user={user} onLogout={handleLogout} onLogin={handleLogin} />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
